import ModernTimeDropdown from '../../dashboard-components/layouts/modernDropdown';
import React, { useState, useEffect } from 'react';
import server from '../../api/server';
import { Play, Pause, Volume2, FilterX } from 'lucide-react';

const ExploreVoicesModal = () => {
  const [voiceSamples, setVoiceSamples] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeAudio, setActiveAudio] = useState(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  
  // Configuration state
  const [configData, setConfigData] = useState({});
  
  // Filter states
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedGender, setSelectedGender] = useState('female'); // Default to female
  const [selectedVoice, setSelectedVoice] = useState('');
  const [samplesLoading, setSamplesLoading] = useState(false);
  
  // Options for dropdowns
  const [providerOptions, setProviderOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [voiceOptions, setVoiceOptions] = useState([]);
  
  // Clean up audio when unmounting
  useEffect(() => {
    return () => {
      if (activeAudio) {
        activeAudio.pause();
        activeAudio.currentTime = 0;
      }
    };
  }, []);
  
  // Fetch voice samples for the selected filters
  const fetchVoiceSamples = async () => {
    if (!selectedProvider) return;
    
    try {
      setSamplesLoading(true);
      const response = await server.getVoiceSamples(
        selectedProvider, 
        selectedModel, 
        selectedGender, 
        selectedVoice
      );
      
      if (response.status === 200) {
        const data = await response.json();
        console.log("Voice samples received:", data);
        setVoiceSamples(data.presigned_url);
      } else {
        setError("Failed to load voice samples");
      }
    } catch (err) {
      console.error("Error fetching voice samples:", err);
      setError("An error occurred while fetching voice samples");
    } finally {
      setSamplesLoading(false);
    }
  };

  // Fetch allowed config on component mount
  useEffect(() => {
    getAllowedConfig();
  }, []);

  // Fetch voice samples when filters change
  useEffect(() => {
    if (selectedProvider && selectedModel && selectedGender && selectedVoice) {
      fetchVoiceSamples();
    }
  }, [selectedProvider, selectedModel, selectedGender, selectedVoice]);

  // Update model options when provider changes
  useEffect(() => {
    if (selectedProvider && configData.voice && configData.voice[selectedProvider]) {
      const models = Object.keys(configData.voice[selectedProvider]);
      setModelOptions(models);
      
      // Reset model selection if current selection is invalid
      if (!models.includes(selectedModel)) {
        setSelectedModel('');
      }
    } else {
      setModelOptions([]);
      setSelectedModel('');
    }
  }, [selectedProvider, configData]);

  // Update voice options when provider, model, and gender change
  useEffect(() => {
    if (
      selectedProvider && 
      selectedModel && 
      selectedGender && 
      configData.voice && 
      configData.voice[selectedProvider] && 
      configData.voice[selectedProvider][selectedModel] &&
      configData.voice[selectedProvider][selectedModel].voices &&
      configData.voice[selectedProvider][selectedModel].voices[selectedGender]
    ) {
      const voices = configData.voice[selectedProvider][selectedModel].voices[selectedGender];
      // Convert voice objects to names for dropdown display
      const voiceNames = voices.map(voice => 
        typeof voice === 'object' && voice.id ? voice.id : voice
      );
      setVoiceOptions(voiceNames);
      
      // Reset voice selection if current selection is invalid
      if (!voiceNames.includes(selectedVoice)) {
        setSelectedVoice('');
      }
    } else {
      setVoiceOptions([]);
      setSelectedVoice('');
    }
  }, [selectedProvider, selectedModel, selectedGender, configData]);

  // Get configuration data
  const getAllowedConfig = async () => {
    try {
      setLoading(true);
      let res = await server.get_voice_agent_allowed_config();
      if(res.status === 200){
        let resp = await res.json();
        console.log("Config data received:", resp.data);
        
        const config = resp.data || {};
        setConfigData(config);
        
        // Set provider options
        const providers = Object.keys(config.voice || {});
        console.log("Provider options:", providers);
        
        if (providers.length > 0) {
          setProviderOptions(providers);
        } else {
          console.warn("No provider options found in config data");
          // Fallback to default providers if none found in API
          setProviderOptions(['elevenlabs', 'openai', 'sarvam']);
        }
      }
    } catch (err) {
      console.error("Error fetching allowed config:", err);
      setError("Failed to load voice configuration");
      // Fallback to default providers in case of error
      setProviderOptions(['elevenlabs', 'openai', 'sarvam']);
    } finally {
      setLoading(false);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSelectedProvider('');
    setSelectedModel('');
    setSelectedGender('female'); // Reset to default female
    setSelectedVoice('');
  };

  // Handle playing audio
  const playAudio = (audioUrl, voiceId) => {
    // If there's already an audio playing, stop it
    if (activeAudio) {
      activeAudio.pause();
      activeAudio.currentTime = 0;
    }
    
    // If we're clicking the same audio that's already playing, just stop it
    if (currentlyPlaying === voiceId) {
      setCurrentlyPlaying(null);
      setActiveAudio(null);
      return;
    }
    
    // Create and play new audio
    const audio = new Audio(audioUrl);
    audio.onended = () => {
      setCurrentlyPlaying(null);
    };
    
    audio.play().catch(e => {
      console.error("Error playing audio:", e);
      setError("Failed to play audio sample");
    });
    
    setActiveAudio(audio);
    setCurrentlyPlaying(voiceId);
  };

  // Loading state for initial config fetch
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-10">
        <div className="loader"></div>
        <div className="mt-4 text-gray-700">Loading voice configurations...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-10">
        <div className="text-red-500 text-xl mb-2">⚠️ Error</div>
        <div className="text-gray-700">{error}</div>
        <div className="mt-4 text-sm text-gray-500">Please try again later</div>
      </div>
    );
  }

  // Main content
  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Voice Explorer</h2>
      
      {/* Filter Bar */}
      <div className="mb-8 border rounded-lg">
        <div className="bg-gray-50 rounded-t-lg px-4 py-3 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">Filter Voice Samples</h3>
          <button 
            onClick={resetFilters}
            className="flex items-center text-gray-500 hover:text-gray-700"
          >
            <FilterX className="w-4 h-4 mr-1" />
            <span className="text-sm">Reset Filters</span>
          </button>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Provider Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
              <ModernTimeDropdown ariaLabel="Provider" value={selectedProvider} onChange={(e) => setSelectedProvider(e.target.value)} options={[['', 'Select Provider'], ...providerOptions]} width="w-full" />
            </div>
            
            {/* Model Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <ModernTimeDropdown ariaLabel="Model" value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} isDisabled={!selectedProvider} options={[['', 'Select Model'], ...modelOptions]} width="w-full" />
            </div>
            
            {/* Gender Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <ModernTimeDropdown ariaLabel="Gender" value={selectedGender} onChange={(e) => {
                  setSelectedGender(e.target.value);
                  setSelectedVoice(''); // Reset voice selection when gender changes
                }} options={['female', 'male']} width="w-full" />
            </div>
            
            {/* Voice Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Voice</label>
              <ModernTimeDropdown ariaLabel="Voice" value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)} isDisabled={!selectedModel || voiceOptions.length === 0} options={[['', 'Select Voice'], ...voiceOptions]} width="w-full" />
            </div>
          </div>
          
          {/* Display selected gender */}
          {selectedGender && (
            <div className="mt-3">
              <span className="text-sm text-blue-700 bg-blue-50 px-2 py-1 rounded">
                Gender: {selectedGender}
              </span>
            </div>
          )}
        </div>
      </div>
      
      {/* Voice Samples Section */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-800">
            Voice Samples
          </h3>
          
          <div className="flex gap-3">
            <button 
              onClick={() => {
                setSelectedGender('female');
                setSelectedVoice(''); // Reset voice selection when gender changes
              }} 
              className={`px-2 py-1 text-xs rounded-md ${
                selectedGender === 'female'
                  ? 'bg-pink-100 text-pink-700' 
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              Female
            </button>
            <button 
              onClick={() => {
                setSelectedGender('male');
                setSelectedVoice(''); // Reset voice selection when gender changes
              }} 
              className={`px-2 py-1 text-xs rounded-md ${
                selectedGender === 'male'
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              Male
            </button>
          </div>
        </div>
        
        <div className="p-4">
          {!selectedProvider ? (
            <div className="text-center py-10 text-gray-500">
              Please select filters to view voice samples
            </div>
          ) : samplesLoading ? (
            <div className="py-8 text-center">
              <div className="loader mx-auto"></div>
              <div className="mt-4 text-gray-700">Loading voice samples...</div>
            </div>
          ) : voiceSamples === null ? (
            <div className="text-center py-10 text-gray-500">
              No voice samples available for the selected filters
            </div>
          ) : voiceSamples !== null ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <audio src={voiceSamples} controls />
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">
              No voice samples available for the selected filters
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExploreVoicesModal;